import { useDebouncedState } from '@mantine/hooks'
import { useRef, useMemo, useState, useCallback } from 'react'

import { type CoreProductSelection } from '@flecto/types'

type UseBookingProductsFilterProps = {
  productsSelected: CoreProductSelection[]
}

export function useBookingProductsFilter({
  productsSelected,
}: UseBookingProductsFilterProps) {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchTerm, setSearchTerm] = useDebouncedState('', 300)

  const INITIAL_ITEMS_TO_LOAD = 50
  const [displayedItemCount, setDisplayedItemCount] = useState(
    INITIAL_ITEMS_TO_LOAD
  )

  const filteredItems = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase()

    return productsSelected.filter((ps) => {
      const isNotBooked = !ps.bookingProduct?.booking?.internal_id
      const matchesSearch =
        !searchTerm ||
        ps.product?.title?.toLowerCase().includes(lowerSearchTerm) ||
        ps.product?.categories?.some((c) =>
          c.toLowerCase().includes(lowerSearchTerm)
        )

      return isNotBooked && matchesSearch
    })
  }, [productsSelected, searchTerm])

  const displayedItems = useMemo(() => {
    const groupedItems = new Map<string, CoreProductSelection[]>()
    let totalItemsAdded = 0

    for (const ps of filteredItems) {
      if (totalItemsAdded >= displayedItemCount) break

      const group = ps?.product?.categories?.join(' > ') || ''

      if (!groupedItems.has(group)) {
        groupedItems.set(group, [])
      }

      if (groupedItems.get(group)!.length < INITIAL_ITEMS_TO_LOAD) {
        groupedItems.get(group)!.push(ps)
        totalItemsAdded++
      }
    }

    return groupedItems
  }, [filteredItems, displayedItemCount])

  const hasMoreItems = useMemo(() => {
    const displayedItemsCount = Array.from(displayedItems.values()).reduce(
      (total, group) => total + group.length,
      0
    )
    return displayedItemsCount < filteredItems.length
  }, [displayedItems, filteredItems.length])

  const handleLoadMore = useCallback(() => {
    setDisplayedItemCount((prev) => prev + INITIAL_ITEMS_TO_LOAD)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchTerm('')
    setDisplayedItemCount(INITIAL_ITEMS_TO_LOAD)
    if (searchInputRef.current) {
      searchInputRef.current.value = ''
    }
  }, [setSearchTerm])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value)
      setDisplayedItemCount(INITIAL_ITEMS_TO_LOAD)
    },
    [setSearchTerm]
  )

  return useMemo(
    () => ({
      searchTerm,
      handleClearSearch,
      searchInputRef,
      handleSearchChange,
      filteredItems,
      displayedItems,
      hasMoreItems,
      handleLoadMore,
    }),
    [
      searchTerm,
      handleClearSearch,
      handleSearchChange,
      filteredItems,
      displayedItems,
      hasMoreItems,
      handleLoadMore,
    ]
  )
}
